<template>
  <div v-show="!loading">
    <div class="mb-4">
      <h4 class="font-weight-bold mb-2">
        <span class="text-muted font-weight-light">
          Administration <span class="material-icons md-14 arrow-east">east</span>
          <router-link class="text-muted" :to="{ name: 'users' }">Users</router-link> <span class="material-icons md-14 arrow-east">east</span>
        </span>
        {{ item.name || 'User name' }}
      </h4>
    </div>

    <div class="row">
      <div class="col-xl-10">
        <form id="administration-user" class="card" @submit.prevent="submit" :class="{'validation-feedback': validation}">
          <div class="card-body">
            <div class="d-flex justify-content-between">
              <h4 class="text-secondary">User details</h4>
              <b-dropdown no-caret type="link" variant="link" class="mb-2" v-if="item && item.id">
                <template #button-content>
                  <span class="monospace text-secondary">...</span>
                </template>
                <template v-if="item && item.id">
                <b-dropdown-item @click="$bvModal.show('audit-log')">
                  <span class="material-symbols-outlined filled p-1" style="vertical-align: sub;">user_attributes</span> Audit log
                </b-dropdown-item>
                <b-dropdown-item @click="startResetTwoFactor">
                  <span class="material-icons p-1" style="vertical-align: sub;">lock_reset</span> Reset two-factor authentication
                </b-dropdown-item>
                </template>
              </b-dropdown>
            </div>
            <div class="form-group row">
              <label for="inputEmail" class="col-sm-2 col-form-label">Email</label>
              <div class="col-sm-10">
                <b-input id="inputEmail" type="email" autocomplete="off" data-1p-ignore v-model="item.email" :disabled="saving" required
                         @input="emailChanged"
                         :state="errors.email && !errors.email"
                         placeholder="email@address.tld"></b-input>
                <b-form-invalid-feedback v-if="errors.email">
                  {{ errors.email.join() }}
                </b-form-invalid-feedback>
              </div>
            </div>
            <div class="form-group row">
              <label for="inputName" class="col-sm-2 col-form-label">Name</label>
              <div class="col-sm-10">
                <b-input id="inputName" v-model="item.name" autocomplete="off" data-1p-ignore :disabled="saving" required
                         :state="errors.name && !errors.name"
                         placeholder="First and last name"></b-input>
                <b-form-invalid-feedback v-if="errors.name">
                  {{ errors.name.join() }}
                </b-form-invalid-feedback>
              </div>
            </div>
            <div class="form-group row" v-if="$root.hasRole('admin.internal') && !loading">
              <div class="col-sm-2 col-form-label pl-0 ml-0 pt-0">
                <label class="col-form-label mb-0" style="">User belongs to company</label><br />
                <i style="font-size: 70%;">Default Exatom</i>
              </div>
              <div class="col-sm-10">
                <multiselect id="company" v-model="itemOrganization" :multiple="true" :max="1"
                             :options="addOrganizationGrant.items.filter(org => org.status === 'ACTIVE')"
                             placeholder="Click to select a company" label="name" track-by="id"/>
                <span class="badge badge-pill badge-danger py-1 px-2" style="transform: scale(75%); transform-origin: left;">ADMIN.INTERNAL ONLY</span>
              </div>
            </div>
            <div class="form-group row" v-if="item.id && !getUserProperty('Google OAuth sub')">
              <div class="col-sm-10 offset-sm-2">
                <div><a href="#" @click.prevent="handleResetPassword">Send forgot my password email</a></div>
                <div><a href="#" @click.prevent v-b-modal.modal-password>Change password</a></div>
              </div>
            </div>
            <div class="form-group row" v-if="item.id && !getUserProperty('Google OAuth sub')">
              <label for="inputStatus" class="col-sm-2 col-form-label">Two-factor authentication</label>
              <div class="col-sm-10 py-2">
                <span :class="item.two_factor_completed_at ? 'text-success' : 'text-danger'">{{ item.two_factor_completed_at ? 'Enabled' : 'Disabled' }}</span>
                <a v-if="item.two_factor_completed_at" href="javascript:void(0)" @click="startResetTwoFactor" class="d-block">Reset two-factor authentication</a>
                <a v-else href="https://support.exatom.io/portal/en/kb/articles/securing-your-account-with-two-factor-authentication" target="_blank" class="d-block">How to enable two-factor authentication?</a>
              </div>
            </div>
            <div class="form-group row" v-if="$route.params.id">
              <label for="inputStatus" class="col-sm-2 col-form-label">Status</label>
              <div class="col-sm-10">
                <b-form-radio-group name="inputStatus" id="inputStatus" v-model="item.status" :disabled="saving"
                                    :options="statusOptions" buttons button-variant="primary"></b-form-radio-group>
              </div>
            </div>
            <div class="form-group row">
              <div class="col-sm-2 text-right">
                <label class="col-form-label mb-0" style="">Roles</label>
              </div>
              <div class="col-sm-10">
                <table class="table table-striped" style="width: auto;">
                  <thead>
                  <tr>
                    <th v-for="role in roles" :key="role.id">
                      <div class="vertical-header ml-1">{{ role.name }}</div>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td v-for="role in roles" :key="role.id" class="text-center">
                      <shield-check class="cursor-pointer shield-icon" :checked="!!itemRoles.latest[role.id]"
                                    @click="itemRoles.latest = { ...itemRoles.latest, [role.id]: !itemRoles.latest[role.id] }"/>
                    </td>
                  </tr>
                  </tbody>
                </table>
                <div class="text-danger mt-3" v-if="errorRoles">Please select one or more roles by clicking the shield icons</div>
              </div>
            </div>
            <div class="form-group row" v-if="item && item.id">
              <label class="col-sm-2 col-form-label">Preferences</label>
              <div class="col-sm-10 mt-2">
                <user-properties :user="item" />
              </div>
            </div>

            <div class="mt-5">
              <div v-if="errorGrants" class="text-danger col-12 mb-3">
                Please select one or more websites {{ $root.hasRole('admin.internal') ? 'or companies ' : '' }}that the user should be able to access.
              </div>
              <div class="row p-3 py-4 mx-3 card-on-top" :class="{ 'bg-danger-light': errorGrants }">
                <div class="col-md-6">
                  <h4 class="text-primary mb-2 d-inline-block">User's access to websites</h4>
                  <i class="material-icons info" v-b-tooltip :title="'Setting permissions over here allows ' + (item.name || 'the user') + ' to only access the websites selected'">info</i>
                  <template v-if="isOrgAdmin && itemOrganization.length">
                    <p>Access to all clients of <b>{{ itemOrganization[0].name }}</b></p>
                  </template>
                  <template v-else-if="isOrgAdmin">
                    <p>Access to all clients of the users company (none selected yet)</p>
                  </template>
                  <div v-if="isOrgAdmin && $root.hasRole('admin.internal')" class="font-weight-bolder text-primary">
                    Additionally access to <span class="badge badge-pill badge-danger py-1 px-2" style="transform: scale(75%); transform-origin: left;">ADMIN.INTERNAL ONLY</span>
                  </div>
                  <grants v-model="itemGrants.clients.latest" v-if="!isOrgAdmin || $root.hasRole('admin.internal')" :class="{ 'mt-2': itemGrants.clients.latest.length }">
                    <template v-slot:buttons>
                      <button type="button" class="btn btn-iso-create" v-b-modal.modal-add-client>
                        <i class="material-icons icon-iso-create">add</i> Add website
                      </button>
                    </template>
                  </grants>
                </div>
                <div class="col-md-6" v-if="$root.hasRole('admin.internal') && !item.organization_hash">
                  <h4 class="text-primary mb-2 d-inline-block" style="vertical-align: top;">User's access to companies</h4>
                  <i class="material-icons info" v-b-tooltip :title="'Setting permissions over here allows ' + (item.name || 'the user') + ' to access all websites associated with the selected companies'">info</i>
                  <span class="badge badge-pill badge-danger py-1 px-2" style="transform: scale(75%); transform-origin: left;">ADMIN.INTERNAL ONLY</span>
                  <template v-if="isInternalAdmin">
                    <p class="mb-3">Access to all</p>
                  </template>
                  <grants v-model="itemGrants.organizations.latest" v-else :class="{ 'mt-2': itemGrants.organizations.latest.length }">
                    <template v-slot:buttons>
                      <button type="button" class="btn btn-iso-create" v-b-modal.modal-add-org>
                        <i class="material-icons icon-iso-create">add</i> Add company
                      </button>
                    </template>
                  </grants>
                </div>
              </div>
            </div>
          </div>
          <div class="card-footer text-right mb-2">
            <b-btn class="btn btn-outline-danger mr-2" @click="cancel">Cancel</b-btn>
            <ladda-btn type="submit" class="btn btn-create mr-2"
                       :loading="saving && invite" :disabled="saving && !invite"
                       @click.native="validation=true; invite=true">
              {{ item.id ? 'Save user' : 'Create user' }} and send invite email
            </ladda-btn>
            <ladda-btn type="submit" class="btn btn-create" v-bind:class="[item.tc_accepted_date!=null ? 'btn-create' : 'text-success']"
                       :loading="saving && !invite" :disabled="saving && invite"
                       @click.native="validation=true; invite=false">
              {{ item.id ? 'Save user' : 'Create user' }}
            </ladda-btn>
          </div>
        </form>
      </div>
    </div>

    <div class="tech-info mt-3">
      <div v-if="item.id"><b>ID:</b> {{ item.id }}</div>
      <div v-if="item.created_at"><b>Created:</b> {{ item.created_at | datetime }}</div>
      <div v-if="item.updated_at"><b>Last update:</b> {{ item.updated_at | datetime }}</div>
    </div>

    <b-modal id="modal-add-client" title="Add website" cancel-variant="white" ok-variant="success"
             @show="addClientGrant.item = null"
             @shown="$refs.addClientInput.$el.querySelector('input').focus()"
             @ok="addClientGrant.item && itemGrants.clients.latest.push(addClientGrant.item)">
      <div class="form-group row">
        <label class="col-sm-2 col-form-label">Website</label>
        <div class="col-sm-10">
          <multiselect
            v-model="addClientGrant.item" :options="availableClientGrants" :hide-selected="true"
            ref="addClientInput" placeholder="Click to select a website" label="name" track-by="id" />
        </div>
      </div>
    </b-modal>

    <b-modal id="modal-add-org" title="Add company" cancel-variant="white" ok-variant="success"
             @show="addOrganizationGrant.item = null"
             @shown="$refs.addOrgInput.$el.querySelector('input').focus()"
             @ok="addOrganizationGrant.item && itemGrants.organizations.latest.push(addOrganizationGrant.item)">
      <div class="form-group row">
        <label class="col-sm-2 col-form-label">Company</label>
        <div class="col-sm-10">
          <multiselect
            v-model="addOrganizationGrant.item" :options="availableOrganizationGrants" :hide-selected="true"
            ref="addOrgInput" placeholder="Click to select a company" label="name" track-by="id" />
        </div>
      </div>
    </b-modal>

    <b-modal id="modal-password" title="Change password" cancel-variant="iso-link" ok-variant="success" ok-title="Change password"
             @show="modalPassword.value = ''"
             @shown="$refs.passwordInput.focus()"
             @ok="handleSubmitPassword">
      <p class="font-weight-bold">Please choose a new password:</p>

      <ul id="passRequirement16" style="font-size: 0.85rem; margin-bottom: 0;">
        <li>
          16 characters or longer with any combination of characters
          <div id="passRequirementOr" class="font-weight-bold m-2 text-secondary">OR</div>
        </li>
      </ul>
      <ul id="passRequirement8" style="margin-bottom: 0;">
        <li id="passRequirement8Chars">At least 8 characters</li>
        <ul id="passRequirement8List">
          <li id="passRequirement8Number">Needs to include a number</li>
          <li id="passRequirement8Lowercase">Needs to include a lowercase letter</li>
          <li id="passRequirement8Uppercase">Needs to include an uppercase letter</li>
          <li id="passRequirement8Special">Needs to include a special character</li>
        </ul>
      </ul>

      <form id="administration-user-setpassword" @submit.prevent="item.password = modalPassword.value; $bvModal.hide('modal-password')" class="mt-3">
        <div class="input-group">
          <b-input type="password" autofocus required v-model="modalPassword.value" id="password" aria-describedby="reveal" ref="passwordInput"
                   autocomplete="new-password"
                   class="form-control" style="height: 36px;"
                   :state="passwordValid"
                   placeholder="Password" pattern="^(((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_]).{8,128})$|^([a-zA-Z0-9\W_\s]{16,128}))$"
                   @input="validatePassword"/>
          <div class="input-group-append">
            <button class="btn btn-secondary reveal" style="line-height: normal;" type="button" id="reveal"
                    @click="togglePassword($event)">
              <i class="material-icons text-white" style="font-size: 1rem;" v-if="!passwordRevealed">visibility_off</i>
              <i class="material-icons text-white" style="font-size: 1rem;" v-else>visibility</i>
            </button>
          </div>
        </div>
        <button type="submit" class="d-none" ref="passwordSubmit" />
      </form>
    </b-modal>

    <b-modal id="audit-log" :title="`Audit log for ${item.name}`" size="xl">
      <AuditLog
        :endpoint="`/users/${item.id}/logs`"
      ></AuditLog>
      <template #modal-footer="{ ok }">
        <b-button size="sm" variant="success" @click="ok()">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
  .vertical-header {
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    text-transform: capitalize;
  }

  .shield-icon {
    width: 16px;
    height: 16px;
  }
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="@/vendor/libs/vue-multiselect/vue-multiselect.scss" lang="scss"></style>

<script>
import Grants from '@/components/Grants'
import LaddaBtn from '@/vendor/libs/ladda/Ladda'
import Multiselect from 'vue-multiselect'
import ShieldCheck from '@/components/ShieldCheck'
import roles from '@/assets/roles.json'
import { diffCollection, parseNameFromEmail } from '@/utils'
import AuditLog from '@/components/AuditLog.vue'
import UserProperties from '@/components/properties/UserProperties.vue'

export default {
  name: 'UserForm',
  components: { AuditLog, Grants, LaddaBtn, Multiselect, ShieldCheck, UserProperties },
  metaInfo: {
    title: 'User management'
  },
  data () {
    return {
      addClientGrant: {
        item: null,
        items: []
      },
      addOrganizationGrant: {
        item: null,
        items: []
      },
      errors: {},
      errorRoles: false,
      errorGrants: false,
      initialEmail: null,
      invite: false,
      item: {
        organization_hash: null,
        status: 'ACTIVE'
      },
      itemGrants: {
        clients: {
          initial: [],
          latest: []
        },
        organizations: {
          initial: [],
          latest: []
        }
      },
      itemRoles: {
        initial: [],
        latest: {}
      },
      loading: false,
      modalPassword: {
        value: null
      },
      roles: [],
      saving: false,
      statusOptions: [
        { text: 'ACTIVE', value: 'ACTIVE' },
        { text: 'INACTIVE', value: 'INACTIVE' }
      ],
      validation: false,
      passwordRevealed: false,
      passwordValid: null
    }
  },
  computed: {
    availableClientGrants () {
      const itemGrantIds = this.itemGrants.clients.latest.map(grant => grant.id)

      return this.addClientGrant.items.filter(item => !itemGrantIds.includes(item.id))
    },
    availableOrganizationGrants () {
      const itemGrantIds = this.itemGrants.organizations.latest.map(grant => grant.id)

      return this.addOrganizationGrant.items.filter(item => !itemGrantIds.includes(item.id))
    },
    isInternalAdmin () {
      return this.roles
        .filter(role => this.itemRoles.latest[role.id])
        .map(role => role.name)
        .includes('admin.internal')
    },
    isOrgAdmin () {
      return this.roles
        .filter(role => this.itemRoles.latest[role.id])
        .map(role => role.name)
        .includes('admin.company')
    },
    itemOrganization: {
      get () {
        return [this.addOrganizationGrant.items.filter(org => org.id === this.item.organization_hash)[0]].filter(Boolean)
      },
      set (organizations) {
        this.item.organization_hash = organizations.map(org => org.id)[0] || null
      }
    }
  },
  async mounted () {
    this.loading = true

    this.addClientGrant.items = (await this.$api.get('/clients')).data.sort((a, b) => a.name > b.name ? 1 : -1)

    if (this.$root.hasRole('admin.internal')) {
      this.addOrganizationGrant.items = (await this.$api.get('/organizations')).data.sort((a, b) => a.name > b.name ? 1 : -1)
    }

    this.roles = (await this.$api.get('/roles')).data
      .filter(role => (!['admin.internal', 'earlyAccess', 'ai.analysis'].includes(role.name)) || this.$root.hasRole('admin.internal'))
      .sort((a, b) => roles.indexOf(a.name) - roles.indexOf(b.name))

    if (this.$route.params.id) {
      try {
        const item = (await this.$api.get(`/users/${this.$route.params.id}`)).data
        this.item = {
          ...item,
          organization_hash: (item.organization || { id: null }).id
        }
        this.initialEmail = this.item.email
        this.itemRoles.initial = this.item.roles.map(role => role.id)
        this.itemRoles.latest = this.itemRoles.initial.reduce((prev, roleId) => ({ ...prev, [roleId]: true }), {})
        if (!this.isInternalAdmin) {
          const { clients, organizations } = (await this.$api.get(`/users/${this.$route.params.id}/grants`)).data
          const clientGrantIds = clients.map(client => client.client_hash)
          const organizationGrantIds = organizations.map(org => org.organization_hash)
          this.itemGrants.clients = {
            initial: clientGrantIds,
            latest: this.addClientGrant.items.filter(client => clientGrantIds.includes(client.id))
          }
          this.itemGrants.organizations = {
            initial: organizationGrantIds,
            latest: this.addOrganizationGrant.items.filter(org => organizationGrantIds.includes(org.id))
          }
        }
      } catch (_) {
        this.$router.replace({ name: 'users' })
      }
    }

    this.initialState = this.hashState()
    this.loading = false
  },
  methods: {
    emailChanged() {
      const { name, email } = parseNameFromEmail(this.item.email, this.item.name)
      this.item.name = name
      this.item.email = email
    },
    hashState () {
      return JSON.stringify([this.item, this.itemRoles])
    },
    async cancel () {
      if (
        this.hashState() === this.initialState ||
        await this.$bvModal.msgBoxConfirm('Are you sure you want to cancel all your changes?', {
          title: 'Discard changes?',
          okVariant: 'danger',
          cancelVariant: 'iso-link'
        })
      ) {
        await this.$router.push({ name: 'users' })
      }
    },
    async handleResetPassword () {
      if (
        !await this.$bvModal.msgBoxConfirm(`An email will be sent to ${this.initialEmail}`, {
          title: 'Send password reset email?',
          okVariant: 'danger',
          cancelVariant: 'iso-link'
        })
      ) {
        return
      }

      await this.$api.resetInit(this.initialEmail)
    },
    async startResetTwoFactor () {
      if (!await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to reset two-factor authentication for ${this.item.name || 'this user'}?`,
        { title: 'Reset two-factor?', okVariant: 'danger', cancelVariant: 'iso-link' }
      )) {
        return
      }
      try {
        await this.$api.delete(`/users/${this.item.id}/two-factor`)
        this.$root.$bvToast.toast('Two-factor authentication has been reset for this user.', {
          title: 'Two-Factor Authentication',
          solid: true,
          variant: 'success'
        })
      } catch (e) {
        this.$root.$bvToast.toast('Failed to reset two-factor authentication.', {
          title: 'Two-Factor Authentication',
          solid: true,
          variant: 'danger'
        })
      }
    },
    handleSubmitPassword (bvModalEvent) {
      bvModalEvent.preventDefault()

      if (this.passwordValid === false) {
        return
      }

      this.$nextTick(() => this.$refs.passwordSubmit.click())
    },
    async submit () {
      if (
        !this.item.id &&
        !this.$root.hasRole('admin.internal') &&
        this.isOrgAdmin &&
        !await this.$bvModal.msgBoxConfirm(`Are you sure you want to add a user with similar permissions like you? ${this.item.name} will be able to administer your organization ${this.$root.me.organization.name}!`, {
          okTitle: 'I understand, please continue',
          okVariant: 'danger',
          cancelTitle: 'No, I’ll change roles',
          cancelVariant: 'iso-link'
        })
      ) {
        return
      }

      // Did they select a role?
      let nbrErrors = 0

      this.errorRoles = false
      if (!Object.values(this.itemRoles.latest).filter(isSelectedRoleActive => isSelectedRoleActive).length) {
        this.errorRoles = true
        nbrErrors++
      }

      this.errorGrants = false
      if (!this.isInternalAdmin && !this.isOrgAdmin && !this.itemGrants.clients.latest.length && !this.itemGrants.organizations.latest.length) {
        // Regular user created
        this.errorGrants = true
        nbrErrors++
      } else if (this.isOrgAdmin && !this.itemGrants.organizations.latest) {
        // Admin.company created
        this.errorGrants = true
        nbrErrors++
      }

      if (nbrErrors) {
        return
      }

      const isAdd = !this.item.id

      this.saving = true
      this.errors = {}

      try {
        if (this.item.id) {
          await this.$api.patch(`/users/${this.item.id}`, this.item)
        } else {
          this.item.id = (await this.$api.post('/users', this.item)).data.id
        }

        await diffCollection(
          this.itemRoles.initial,
          Object.keys(this.itemRoles.latest).filter(roleId => this.itemRoles.latest[roleId]),
          roleId => this.$api.post(`/users/${this.item.id}/roles`, { roles: [roleId] }),
          roleId => this.$api.delete(`/users/${this.item.id}/roles/${roleId}`)
        )

        !this.isInternalAdmin && await diffCollection(
          this.itemGrants.clients.initial,
          this.itemGrants.clients.latest.map(client => client.id),
          hash => this.$api.post(`/users/${this.item.id}/grants/clients`, { client_hash: hash }),
          hash => this.$api.delete(`/users/${this.item.id}/grants/clients/${hash}`)
        )

        this.$root.hasRole('admin.internal') && await diffCollection(
          this.itemGrants.organizations.initial,
          this.itemGrants.organizations.latest.map(organization => organization.id),
          hash => this.$api.post(`/users/${this.item.id}/grants/organizations`, { organization_hash: hash }),
          hash => this.$api.delete(`/users/${this.item.id}/grants/organizations/${hash}`)
        )

        if (this.invite) {
          await this.$api.post(`/users/${this.item.id}/invite`, {})
        }

        if (
          isAdd &&
          await this.$bvModal.msgBoxConfirm('Do you want to add another user with similar settings?', {
            okVariant: 'success',
            okTitle: 'Yes',
            cancelVariant: 'iso-link',
            cancelTitle: 'No',
          })
        ) {
          this.item.id = null
          this.item.email = ''
          this.item.name = ''
          this.saving = false
          this.validation = false
          return
        }

        this.$router.push({ name: 'users' })
      } catch (error) {
        this.errors = error.response.data.errors
        this.saving = false
      }
    },
    togglePassword (event) {
      document.getElementById('password').type = document.getElementById('password').type === 'password' ? 'text' : 'password'
      if (document.getElementById('password').type === 'password') {
        document.getElementById('password').classList.remove('monospace')
      } else {
        document.getElementById('password').classList.add('monospace')
      }
      this.passwordRevealed = !this.passwordRevealed
    },
    validatePassword (value) {
      // Reset
      this.passwordValid = null;
      ['passRequirement16', 'passRequirementOr', 'passRequirement8', 'passRequirement8Chars', 'passRequirement8List', 'passRequirement8Number', 'passRequirement8Lowercase', 'passRequirement8Uppercase', 'passRequirement8Special'].forEach((el) => {
        if (el !== 'passRequirementOr') {
          document.getElementById(el).className = ''
          document.getElementById(el).style.display = document.getElementById(el).tagName.toLowerCase() === 'ul' ? 'block' : 'list-item'
        } else {
          document.getElementById(el).style.display = 'block'
        }
      })

      if (value.length > 15) {
        ['passRequirementOr', 'passRequirement8'].forEach((el) => {
          document.getElementById(el).style.display = 'none'
        })
        document.getElementById('passRequirement16').classList.add('text-success')
        this.passwordValid = true
      } else if (value.length) {
        let score = 0
        if (value.length > 7) {
          document.getElementById('passRequirement8Chars').classList.add('text-success')
          score++
        } else {
          document.getElementById('passRequirement8Chars').classList.add('text-danger')
        }
        if (value.match(/[0-9]+/)) {
          document.getElementById('passRequirement8Number').classList.add('text-success')
          score++
        }
        if (value.match(/[a-z]+/)) {
          document.getElementById('passRequirement8Lowercase').classList.add('text-success')
          score++
        }
        if (value.match(/[A-Z]+/)) {
          document.getElementById('passRequirement8Uppercase').classList.add('text-success')
          score++
        }
        if (value.match(/[^a-zA-Z0-9]+/)) {
          document.getElementById('passRequirement8Special').classList.add('text-success')
          score++
        }
        if (score === 5) {
          document.getElementById('passRequirement16').style.display = 'none'
          this.passwordValid = true
        }
      }
    },
    getUserProperty (lookupValue) {
      return this.item.properties.find(prop => prop.type.name === lookupValue)
    }
  }
}
</script>

<style>
.modal-body {
  padding-bottom: 0;
}
</style>
